import { Age, ServicePlanId } from '../common';
import { formatPrice } from '../util/commonUtil';

interface Serializable<T> {
  deserialize(input: Object): T;
}

enum CardType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  NO_CARD = 'NO_CARD',
}

interface LanguageVersions {
  fi: string;
  sv: string;
  en: string;
}

interface LanguageVersionLists {
  fi: string[];
  sv: string[];
  en: string[];
}

interface Price {
  price: string;
  minAge: number;
  maxAge: number | null;
  campaign: boolean;
  descriptions: LanguageVersionLists | null;
}

export class ServicePlan implements Serializable<ServicePlan> {
  id: ServicePlanId;
  title: LanguageVersions;
  description: LanguageVersions;
  productImageUrl: string;
  prices: {
    otherCustomer: Price[];
    ownerCustomer: Price[];
    ownerPlus: Price[];
  };
  cardImages: [
    {
      stockId: string;
      name: LanguageVersions;
      imageUrl: string;
      default: boolean;
    },
  ];
  cardType: CardType;
  creditLimitMin: number;
  creditLimitMax: number;
  currentAccount: {
    interestRateOtherCustomer: string;
    interestRateOwnerCustomer: string;
  };

  deserialize(input: ServicePlan) {
    // Campaign prices are not used right now -> filter them out completely
    const otherCustomerPrices = input.prices.otherCustomer.filter((price) => !price.campaign);
    const ownerCustomerPrices = input.prices.ownerCustomer.filter((price) => !price.campaign);
    const ownerPlusPrices = input.prices.ownerPlus.filter((price) => !price.campaign);
    const prices = { otherCustomer: otherCustomerPrices, ownerCustomer: ownerCustomerPrices, ownerPlus: ownerPlusPrices };

    this.id = input.id;
    this.title = input.title;
    this.description = input.description;
    this.productImageUrl = input.productImageUrl;
    this.prices = prices;
    this.cardImages = input.cardImages;
    this.cardType = input.cardType;
    this.creditLimitMin = input.creditLimitMin;
    this.creditLimitMax = input.creditLimitMax;
    this.currentAccount = input.currentAccount;
    return this;
  }

  getTitle(lang: string) {
    switch (lang) {
      case 'sv':
        return this.title.sv;
      case 'en':
        return this.title.en;
      case 'fi':
      default:
        return this.title.fi;
    }
  }

  getOtherCustomerPrice(ageGroup: Age): string {
    return this.getPrice(ageGroup, this.prices.otherCustomer);
  }

  getOwnerCustomerPrice(ageGroup: Age): string {
    return this.getPrice(ageGroup, this.prices.ownerCustomer);
  }

  getOwnerPlusPrice(ageGroup: Age): string {
    return this.getPrice(ageGroup, this.prices.ownerPlus);
  }

  private getPrice(ageGroup: Age, priceForCustomerType: Price[]): string {
    let price: string = '';
    switch (ageGroup) {
      case Age.AGE_18_TO_25:
        for (const priceObj of priceForCustomerType) {
          if (priceObj.minAge >= 18 && priceObj.maxAge && priceObj.maxAge <= 25) {
            price = priceObj.price;
            break;
          }
        }
        break;
      case Age.AGE_26_TO_29:
        for (const priceObj of priceForCustomerType) {
          if (priceObj.minAge >= 26 && priceObj.maxAge && priceObj.maxAge <= 29) {
            price = priceObj.price;
            break;
          }
        }
        break;
      case Age.AGE_OVER_30:
      default:
        for (const priceObj of priceForCustomerType) {
          if (priceObj.minAge >= 30 && !priceObj.maxAge) {
            price = priceObj.price;
            break;
          }
        }
    }
    if (price) return formatPrice(price);
    else throw new Error('Could not get price for age group.');
  }

  // Returns true if there is a campaign in at least one of the customer types and minAge is what's the argument.
  isCampaign(minAge: number): boolean {
    const isCampaignForCustomerType = (pricesForCustomerType: Price[]): boolean => {
      return pricesForCustomerType.some((ageGroup) => {
        if (ageGroup.minAge === minAge) return ageGroup.campaign;
        else return false;
      });
    };

    const prices = this.prices;
    const isOtherCustomerCampaign = isCampaignForCustomerType(prices.otherCustomer);
    const isOwnerCustomerCampaign = isCampaignForCustomerType(prices.ownerCustomer);
    const isOwnerPlusCampaign = isCampaignForCustomerType(prices.ownerPlus);
    return (isOtherCustomerCampaign || isOwnerCustomerCampaign || isOwnerPlusCampaign) ?? false;
  }
}
